import { useEffect, useState } from 'react'
import { ArrowDownTrayIcon, PhoneArrowDownLeftIcon, PlayIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import RozmowaService from '@/services/RozmowaService'
import {
	formatCallDuration,
	getRecordingUrl,
	getTwilioCallDuration,
	getTwilioCallStatus,
} from './rozmowaDetailsHelpers'

const triggerBlobDownload = (blob, filename) => {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename || 'recording'
	document.body.appendChild(link)
	link.click()
	link.remove()
	URL.revokeObjectURL(url)
}

export default function RozmowaCallResultCard({ rozmowa }) {
	const { t } = useTranslation()
	const [downloading, setDownloading] = useState(false)
	const [loadingPlayback, setLoadingPlayback] = useState(false)
	const [recordingBlob, setRecordingBlob] = useState(null)
	const [recordingObjectUrl, setRecordingObjectUrl] = useState(null)
	const [showPlayer, setShowPlayer] = useState(false)

	const rozmowaId = rozmowa?._id || rozmowa?.id
	const callStatus = getTwilioCallStatus(rozmowa)
	const callDurationSeconds = getTwilioCallDuration(rozmowa)
	const callDuration = formatCallDuration(callDurationSeconds)
	const hasRecording = Boolean(getRecordingUrl(rozmowa))
	const loadingRecording = downloading || loadingPlayback

	const statusLabel = callStatus
		? t(`conversations.details.callStatus.${callStatus}`, {
				defaultValue: callStatus,
			})
		: t('conversations.details.callResult.emptyStatus')

	useEffect(() => {
		setRecordingBlob(null)
		setShowPlayer(false)
		setRecordingObjectUrl(previousUrl => {
			if (previousUrl) URL.revokeObjectURL(previousUrl)
			return null
		})
	}, [rozmowaId])

	useEffect(() => {
		return () => {
			if (recordingObjectUrl) URL.revokeObjectURL(recordingObjectUrl)
		}
	}, [recordingObjectUrl])

	const fetchRecording = async () => {
		if (recordingBlob) {
			return { success: true, blob: recordingBlob, filename: null, error: null }
		}

		const result = await RozmowaService.downloadRecording(rozmowaId)
		if (result.success && result.blob) {
			setRecordingBlob(result.blob)
		}

		return result
	}

	const handleDownloadRecording = async () => {
		if (!hasRecording || loadingRecording) return

		setDownloading(true)
		const result = await fetchRecording()
		setDownloading(false)

		if (!result.success || !result.blob) {
			await Swal.fire(
				t('conversations.messages.errorTitle'),
				result.error || t('conversations.details.messages.downloadRecordingFailed'),
				'error'
			)
			return
		}

		triggerBlobDownload(result.blob, result.filename)
	}

	const handlePlayRecording = async () => {
		if (!hasRecording || loadingRecording) return

		if (recordingObjectUrl) {
			setShowPlayer(true)
			return
		}

		setLoadingPlayback(true)
		const result = await fetchRecording()
		setLoadingPlayback(false)

		if (!result.success || !result.blob) {
			await Swal.fire(
				t('conversations.messages.errorTitle'),
				result.error || t('conversations.details.messages.playRecordingFailed'),
				'error'
			)
			return
		}

		const objectUrl = URL.createObjectURL(result.blob)
		setRecordingObjectUrl(objectUrl)
		setShowPlayer(true)
	}

	return (
		<div className="mb-5 rounded-md border border-slate-200 bg-white shadow-sm">
			<div className="border-b border-slate-300 bg-gray-100 px-6 py-3">
				<h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
					<PhoneArrowDownLeftIcon className="h-5 w-5 text-sky-700" />
					<span>{t('conversations.details.callResult.title')}</span>
				</h2>
			</div>
			<div className="px-6 py-6">
				<dl className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div>
						<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
							{t('conversations.details.fields.callStatus')}
						</dt>
						<dd className="mt-0.5 text-sm text-gray-900">{statusLabel}</dd>
					</div>
					<div>
						<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
							{t('conversations.details.fields.callDuration')}
						</dt>
						<dd className="mt-0.5 text-sm text-gray-900">
							{callDuration || t('conversations.details.callResult.emptyDuration')}
						</dd>
					</div>
				</dl>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<button
						type="button"
						onClick={handlePlayRecording}
						disabled={!hasRecording || loadingRecording}
						className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold shadow-sm transition ${
							hasRecording && !loadingRecording
								? 'bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
								: 'cursor-not-allowed bg-gray-300 text-gray-500'
						}`}>
						<PlayIcon className="h-5 w-5" />
						<span>
							{loadingPlayback
								? t('conversations.details.actions.loadingRecording')
								: t('conversations.details.actions.playRecording')}
						</span>
					</button>
					<button
						type="button"
						onClick={handleDownloadRecording}
						disabled={!hasRecording || loadingRecording}
						className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold shadow-sm transition ${
							hasRecording && !loadingRecording
								? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
								: 'cursor-not-allowed bg-gray-300 text-gray-500'
						}`}>
						<ArrowDownTrayIcon className="h-5 w-5" />
						<span>
							{downloading
								? t('conversations.details.actions.downloadingRecording')
								: t('conversations.details.actions.downloadRecording')}
						</span>
					</button>
				</div>

				{!hasRecording ? (
					<p className="mt-3 text-sm text-gray-500">{t('conversations.details.callResult.recordingUnavailable')}</p>
				) : null}

				{showPlayer && recordingObjectUrl ? (
					<div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
						<audio controls autoPlay className="w-full" src={recordingObjectUrl}>
							{t('conversations.details.messages.playRecordingUnsupported')}
						</audio>
					</div>
				) : null}
			</div>
		</div>
	)
}
